"use client";

import { useEffect, useRef } from "react";
import { usePrefersLight } from "@/hooks/use-color-scheme";
import { TRUSTPILOT_URL } from "@/lib/constants";

declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (el: Element, forceReload?: boolean) => void;
    };
  }
}

const BOOTSTRAP_SRC = "https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
const BOOTSTRAP_ID = "trustpilot-bootstrap";
const REVIEW_URL = TRUSTPILOT_URL;

let bootstrapPromise: Promise<void> | null = null;

function loadTrustpilotBootstrap(): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("ssr"));
  if (window.Trustpilot) return Promise.resolve();
  if (bootstrapPromise) return bootstrapPromise;

  bootstrapPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(BOOTSTRAP_ID) as HTMLScriptElement | null;
    if (existing) {
      if (window.Trustpilot) return resolve();
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("trustpilot script error")), {
        once: true,
      });
      return;
    }
    const s = document.createElement("script");
    s.id = BOOTSTRAP_ID;
    s.src = BOOTSTRAP_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      bootstrapPromise = null;
      reject(new Error("trustpilot script error"));
    };
    document.body.appendChild(s);
  });

  return bootstrapPromise;
}

export function useTrustpilotLazyLoad(
  ref: React.RefObject<HTMLDivElement | null>,
  reloadKey?: unknown
) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const trigger = () => {
      loadTrustpilotBootstrap()
        .then(() => {
          if (ref.current && window.Trustpilot) {
            window.Trustpilot.loadFromElement(ref.current, true);
          }
        })
        .catch(() => {});
    };

    if (typeof IntersectionObserver === "undefined") {
      trigger();
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          trigger();
        }
      },
      { rootMargin: "200px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, reloadKey]);
}

export function TrustpilotMini({ className }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const prefersLight = usePrefersLight();
  useTrustpilotLazyLoad(ref, prefersLight);

  return (
    <div
      key={prefersLight ? "light" : "dark"}
      ref={ref}
      className={`trustpilot-widget ${className ?? ""}`}
      data-locale="en-US"
      data-template-id="53aa8807dec7e10d38f59f32"
      data-businessunit-id="626d5b5575edfb812c69a5e0"
      data-style-height="90px"
      data-style-width="100%"
      data-theme={prefersLight ? "light" : "dark"}
      data-token="0f9bf0c8-3074-49c4-b5e5-60a9f1ad0431"
    >
      <a href={REVIEW_URL} target="_blank" rel="noopener noreferrer">
        Trustpilot
      </a>
    </div>
  );
}

export function TrustpilotMicroButton({ className }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useTrustpilotLazyLoad(ref);

  return (
    <div
      ref={ref}
      className={`trustpilot-widget ${className ?? ""}`}
      data-locale="en-US"
      data-template-id="5419b757fa0340045cd0c938"
      data-businessunit-id="626d5b5575edfb812c69a5e0"
      data-style-height="25px"
      data-style-width="100%"
      data-token="a012992b-fcb2-42a3-afec-eeb66155f167"
    >
      <a href={REVIEW_URL} target="_blank" rel="noopener noreferrer">
        Trustpilot
      </a>
    </div>
  );
}
